import { ReactNode, createContext, useContext } from 'react'

// Initial value for an uninitialized container context
const EMPTY = Symbol()

type SafeContainerProviderProps<S = undefined> = S extends undefined
  ? {
      children: ReactNode
    }
  : {
      children: ReactNode
      state: S
    }

/**
 * Create a context container based on the arguments & return values of a given hook.
 *
 * Unlike the `unstated-next` library that we currently leverage, this module will return a `Provider` component that
 * enforces a non-optional state argument that is passed to the provided hook. This allows us to write our container hooks
 * in a way where we do not need to handle the case where the hook argument is undefined, and will force you to pass a `state`
 * argument to the provider that cooresponds to the expected argument type of the container hook.
 *
 * // Container hooks can be defined without optional arguments, unlike with `unstated-next`
 * const CounterHook = createSafeContainer((quantity: number) => {})
 *
 * // Not allowed, because the container hook defines a non-optional argument
 * <CounterHook.Provider>...</CounterHook.Provider>
 *
 * // You must pass the `initialCount` argument to the provider state prop.
 * <CounterHook.Provider state={5}>...</CounterHook.Provider>
 */
export function createSafeContainer<V, S = undefined>(containerHook: (state: S) => V) {
  const ctx = createContext<V | typeof EMPTY>(EMPTY)

  function Provider(props: SafeContainerProviderProps<S>) {
    const value = containerHook(('state' in props ? props.state : undefined) as S)
    return <ctx.Provider value={value}>{props.children}</ctx.Provider>
  }

  function useContainer() {
    const value = useContext(ctx)
    if (value === EMPTY) {
      throw new Error(
        `useContainer must be used within a Provider ${
          containerHook.name ? `(${containerHook.name})` : ''
        }`
      )
    }
    return value
  }

  return {
    Provider,
    useContainer,
  }
}
